/* eslint-disable  @typescript-eslint/no-explicit-any */
import { useEffect, useRef, useState } from 'react';

import { Form, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { InfoCircle } from 'react-bootstrap-icons';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import Select from 'react-select';

import { getComponentFormError } from '../../../utils';

import './style.scss';

interface Props {
  label: string;
  name: string;
  description?: string;
  defaultValue?: any;
  code: any;
  display: any;
  data?: Array<any>;
  placeholder?: string;
  whenChange?: (selected: any) => void;
  child?: string;
  customError?: any;
  readOnly?: boolean;
  selectFirstOption?: boolean;
}

const Combobox = (props: Props) => {
  const {
    formState: { errors },
    setValue,
    control,
    getValues
  } = useFormContext();
  const { t } = useTranslation();
  const {
    label,
    name,
    description,
    code,
    display,
    data,
    placeholder,
    defaultValue,
    whenChange,
    customError,
    readOnly,
    // usamos como opción por defecto que quede elegido
    selectFirstOption = true
  } = props;

  const error = getComponentFormError(name, errors);
  const [thisDefault, setDefault] = useState(undefined);
  const selectOptionRef = useRef<((selected: any) => void) | undefined>(undefined);
  const automaticallySelectedRef = useRef<{ code: any } | null>(null);

  useEffect(() => {
    const nativeDefault = getValues(name);

    const hasDefaultValue = defaultValue?.[code] !== undefined && defaultValue?.[code] !== null;
    const hasNativeValue = nativeDefault?.[code] !== undefined && nativeDefault?.[code] !== null;
    if (hasDefaultValue || hasNativeValue) {
      const selected = data?.find(
        (x) =>
          (defaultValue && x[code] === defaultValue[code]) ||
          (nativeDefault && nativeDefault[code] === x[code])
      );

      const wasAutomaticallySelected = automaticallySelectedRef.current?.code === selected?.[code];
      if (selected !== undefined && whenChange && !wasAutomaticallySelected) {
        whenChange(selected);
      }

      setDefault(selected);

      if (hasNativeValue || hasDefaultValue) {
        if (selected !== undefined) {
          setValue(name, selected);
        }
      } else {
        setValue(name, null);
      }
    } else if (selectFirstOption && data?.length === 1) {
      const firstOption = data[0];
      const wasFirstOptionAutomaticallySelected =
        automaticallySelectedRef.current?.code === firstOption[code];

      if (!wasFirstOptionAutomaticallySelected) {
        automaticallySelectedRef.current = { code: firstOption[code] };
        selectOptionRef.current?.(firstOption);
      }
    }
  }, [code, data, defaultValue, getValues, name, selectFirstOption, setValue, whenChange]);

  return (
    <Form.Group>
      <Form.Label>
        {label}{' '}
        {description ? (
          <OverlayTrigger overlay={<Tooltip id={`tooltip-${name}`}>{description}</Tooltip>}>
            <span className="d-inline-block">
              <InfoCircle className="small"></InfoCircle>
            </span>
          </OverlayTrigger>
        ) : (
          <></>
        )}
      </Form.Label>
      <div className={error || customError ? 'is-invalid' : ''}>
        <Controller
          defaultValue={thisDefault}
          name={name}
          control={control}
          render={({ field }) => {
            selectOptionRef.current = (selected: any) => {
              if (whenChange) {
                if (props.child) {
                  setValue(props.child, null);
                }
                whenChange(selected);
              }
              field.onChange(selected);
            };

            return (
              <Select
                styles={{
                  menu: (provided) => ({
                    ...provided,
                    backgroundColor: 'var(--form-input-bg, #fbfbfc)',
                    zIndex: 9999
                  }),
                  menuList: (provided) => ({
                    ...provided,
                    backgroundColor: 'var(--form-input-bg, #fbfbfc)'
                  }),
                  option: (provided, state) => ({
                    ...provided,
                    backgroundColor: state.isSelected
                      ? 'var(--gray-500, #adb5bd)'
                      : state.isFocused
                        ? 'var(--primary, #003a75)'
                        : 'var(--form-input-bg, #fbfbfc)',
                    color:
                      state.isSelected || state.isFocused
                        ? 'var(--light, #f8f9fa)'
                        : 'var(--form-input-color, #212529)',
                    ':active': {
                      ...provided[':active'],
                      backgroundColor: state.isSelected
                        ? 'var(--gray-500, #adb5bd)'
                        : 'var(--primary, #003a75)',
                      color: 'var(--light, #f8f9fa)'
                    }
                  })
                }}
                defaultValue={thisDefault}
                options={data}
                className="react-select"
                classNamePrefix="react-select "
                value={field.value}
                getOptionLabel={(item: any) => item[display]}
                getOptionValue={(item: any) => item[code]}
                placeholder={placeholder ? placeholder : ' '}
                noOptionsMessage={() => t('common.label.noFound')}
                isClearable={true}
                isDisabled={readOnly}
                onChange={selectOptionRef.current}
                onBlur={field.onBlur}
                ref={field.ref}
              />
            );
          }}
        />
      </div>

      <Form.Control.Feedback type="invalid">
        {error ? error.message : customError ? customError.message : ''}
      </Form.Control.Feedback>
    </Form.Group>
  );
};

export default Combobox;
